import axios from "axios";
import { getAccessToken } from "./authSession";
import { API_BASE_URL } from "../config";

const getAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }

    return {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
};

const cleanParams = (params = {}) =>
    Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""),
    );

export const getCatalogProducts = async (params) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/products`, {
        ...getAuthHeaders(),
        params: cleanParams(params),
    });
    return response.data;
};

export const getCatalogProductDetail = async (sku) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}`, getAuthHeaders());
    return response.data;
};
export const getCatalogWholesaleMappings = async (sku) => (
    await axios.get(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/wholesale-mappings`, getAuthHeaders())
).data;
export const updateCatalogWholesaleMapping = async (id, data) => (
    await axios.patch(`${API_BASE_URL}/catalog/wholesale-mappings/${id}`, data, getAuthHeaders())
).data;

export const createCatalogAdditionalBarcode = async (sku, data) => (
    await axios.post(API_BASE_URL + "/catalog/products/" + encodeURIComponent(sku) + "/barcodes", data, getAuthHeaders())
).data;
export const updateCatalogAdditionalBarcode = async (sku, barcodeId, data) => (
    await axios.patch(API_BASE_URL + "/catalog/products/" + encodeURIComponent(sku) + "/barcodes/" + barcodeId, data, getAuthHeaders())
).data;

export const updateCatalogProduct = async (sku, data) => {
    const response = await axios.patch(
        `${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}`,
        data,
        getAuthHeaders(),
    );
    return response.data;
};

export const updateCatalogImageMarketplaceAccess = async (sku, imageId, isMarketplaceAllowed) => (
    await axios.patch(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/images/${imageId}`, { is_marketplace_allowed: isMarketplaceAllowed }, getAuthHeaders())
).data;

export const makeCatalogImageMain = async (sku, imageId) => (
    await axios.post(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/images/${imageId}/make-main`, {}, getAuthHeaders())
).data;

export const uploadCatalogImage = async (sku, file) => {
    const token = getAccessToken();
    const data = new FormData(); data.append("file", file);
    return (await axios.post(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/images`, data, { headers: { Authorization: `Bearer ${token}` } })).data;
};
export const deleteCatalogImage = async (sku, imageId) => (
    await axios.delete(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/images/${imageId}`, getAuthHeaders())
).data;
export const reorderCatalogImages = async (sku, imageIds) => (
    await axios.post(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/images/reorder`, { image_ids: imageIds }, getAuthHeaders())
).data;
export const saveManualCatalogContent = async (sku, data) => (
    await axios.put(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/content/manual`, data, getAuthHeaders())
).data;
export const deleteManualCatalogContent = async (sku, languageCode) => (
    await axios.delete(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/content/manual/${languageCode}`, getAuthHeaders())
).data;
export const saveCatalogManualFields = async (sku, data) => (
    await axios.patch(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/manual-fields`, data, getAuthHeaders())
).data;
export const clearCatalogManualFields = async (sku, fields) => (
    await axios.post(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/manual-fields/clear`, { fields }, getAuthHeaders())
).data;

export const getCatalogAttributeDefinitions = async (params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/attribute-definitions`, {
        ...getAuthHeaders(),
        params: cleanParams(params),
    });
    return response.data;
};
export const createCatalogAttributeDefinition = async (data) => (await axios.post(`${API_BASE_URL}/catalog/attribute-definitions`, data, getAuthHeaders())).data;
export const updateCatalogAttributeDefinition = async (id, data) => (await axios.patch(`${API_BASE_URL}/catalog/attribute-definitions/${id}`, data, getAuthHeaders())).data;
export const getCatalogAttributeValues = async (definitionId, params = {}) => (await axios.get(`${API_BASE_URL}/catalog/attribute-definitions/${definitionId}/values`, { ...getAuthHeaders(), params: cleanParams(params) })).data;
export const createCatalogAttributeValue = async (definitionId, data) => {
    const valueUa = String(data?.value_ua ?? data?.value ?? "").trim();
    if (!valueUa) {
        throw new Error("Укажите значение на украинском языке");
    }
    return (await axios.post(`${API_BASE_URL}/catalog/attribute-definitions/${definitionId}/values`, { ...data, value_ua: valueUa }, getAuthHeaders())).data;
};
export const updateCatalogAttributeValue = async (id, data) => (await axios.patch(`${API_BASE_URL}/catalog/attribute-values/${id}`, data, getAuthHeaders())).data;
export const getCatalogSupplierAttributeRules = async (supplierId) => (await axios.get(`${API_BASE_URL}/catalog/supplier-attribute-rules`, { ...getAuthHeaders(), params: { supplier_id: supplierId } })).data;
export const getCatalogSupplierAttributeSources = async () => (await axios.get(`${API_BASE_URL}/catalog/supplier-attribute-sources`, getAuthHeaders())).data;
export const createCatalogSupplierAttributeRule = async (data) => (await axios.post(`${API_BASE_URL}/catalog/supplier-attribute-rules`, data, getAuthHeaders())).data;
export const getCatalogSupplierAttributeValueMappings = async (ruleId) => (await axios.get(`${API_BASE_URL}/catalog/supplier-attribute-rules/${ruleId}/value-mappings`, getAuthHeaders())).data;
export const createCatalogSupplierAttributeValueMapping = async (ruleId, data) => (await axios.post(`${API_BASE_URL}/catalog/supplier-attribute-rules/${ruleId}/value-mappings`, data, getAuthHeaders())).data;
export const getCatalogAttributeMappingInfo = async (definitionId) => (await axios.get(`${API_BASE_URL}/catalog/attribute-definitions/${definitionId}/mapping-info`, getAuthHeaders())).data;
export const getCatalogSupplierAttributeUnresolved = async () => (await axios.get(`${API_BASE_URL}/catalog/supplier-attribute-unresolved`, getAuthHeaders())).data;
export const runCatalogAttributeIngestion = async () => (await axios.post(`${API_BASE_URL}/catalog/attribute-ingestion?dry_run=false`, null, getAuthHeaders())).data;
export const importCatalogAttributeDefinitionsXml = async (file, definitionId = null) => {
    const token = getAccessToken(); const data = new FormData(); data.append("file", file);
    return (await axios.post(`${API_BASE_URL}/catalog/attribute-definitions/import-xml`, data, { headers: { Authorization: `Bearer ${token}` }, params: definitionId ? { definition_id: definitionId } : {} })).data;
};

export const getCatalogSupplierAttributeFields = async (params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/supplier-attribute-fields`, { ...getAuthHeaders(), params: cleanParams(params) });
    return response.data;
};

// List attribute rules by scope: {} → all, {supplier_id} → one supplier,
// {scope: "global"} → global rules, {scope: "supplier"} → per-supplier rules.
export const getCatalogAttributeRules = async (params = {}) =>
    (await axios.get(`${API_BASE_URL}/catalog/supplier-attribute-rules`, { ...getAuthHeaders(), params: cleanParams(params) })).data;

export const patchCatalogSupplierAttributeRule = async (ruleId, data) =>
    (await axios.patch(`${API_BASE_URL}/catalog/supplier-attribute-rules/${ruleId}`, data, getAuthHeaders())).data;

export const mergeCatalogAttributeDefinition = async (definitionId, targetId) =>
    (await axios.post(`${API_BASE_URL}/catalog/attribute-definitions/${definitionId}/merge-into/${targetId}`, null, getAuthHeaders())).data;

export const createCatalogProductAttribute = async (sku, data) => {
    const response = await axios.post(
        `${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/attributes`,
        data,
        getAuthHeaders(),
    );
    return response.data;
};

export const updateCatalogProductAttribute = async (sku, attributeId, data) => {
    const response = await axios.patch(
        `${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/attributes/${attributeId}`,
        data,
        getAuthHeaders(),
    );
    return response.data;
};

export const deleteCatalogProductAttribute = async (sku, attributeId) =>
    axios.delete(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/attributes/${attributeId}`, getAuthHeaders());

export const actOnCatalogAttributeCandidate = async (sku, candidateId, action) => {
    const response = await axios.post(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/attribute-candidates/${candidateId}`, { action }, getAuthHeaders());
    return response.data;
};

export const createCatalogProductKeyword = async (sku, data) => {
    const response = await axios.post(
        `${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/keywords`,
        data,
        getAuthHeaders(),
    );
    return response.data;
};

export const updateCatalogProductKeyword = async (sku, keywordId, data) => {
    const response = await axios.patch(
        `${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/keywords/${keywordId}`,
        data,
        getAuthHeaders(),
    );
    return response.data;
};

export const deleteCatalogProductKeyword = async (sku, keywordId) =>
    axios.delete(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/keywords/${keywordId}`, getAuthHeaders());

export const importCatalogAiKeywords = async (file) => {
    const token = getAccessToken();
    const data = new FormData();
    data.append("file", file);
    return (await axios.post(`${API_BASE_URL}/catalog/ai-enrichment/keywords/import`, data, {
        headers: { Authorization: `Bearer ${token}` },
    })).data;
};

export const approveCatalogProductKeyword = async (sku, keywordId) =>
    (await axios.post(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/keywords/${keywordId}/approve`, null, getAuthHeaders())).data;

export const approvePendingCatalogAiKeywords = async (sku) =>
    (await axios.post(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/keywords/approve-pending-ai`, null, getAuthHeaders())).data;

export const getCatalogOfferBlocks = async (params) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/offer-blocks`, {
        ...getAuthHeaders(),
        params: cleanParams(params),
    });
    return response.data;
};

export const createCatalogProductOfferBlock = async (sku, data) => {
    const response = await axios.post(
        `${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/offer-blocks`,
        data,
        getAuthHeaders(),
    );
    return response.data;
};

export const deactivateCatalogOfferBlock = async (blockId) => {
    const response = await axios.patch(`${API_BASE_URL}/catalog/offer-blocks/${blockId}`, {}, getAuthHeaders());
    return response.data;
};

export const getCatalogCategories = async () => {
    const response = await axios.get(`${API_BASE_URL}/catalog/categories`, getAuthHeaders());
    return response.data;
};

export const updateCatalogCategory = async (categoryCode, data) => {
    const response = await axios.patch(
        `${API_BASE_URL}/catalog/categories/${encodeURIComponent(categoryCode)}`,
        data,
        getAuthHeaders(),
    );
    return response.data;
};

export const getCatalogMappings = async (params) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/mappings`, {
        ...getAuthHeaders(),
        params: cleanParams(params),
    });
    return response.data;
};

export const updateCatalogMapping = async (mappingId, data) => {
    const response = await axios.patch(`${API_BASE_URL}/catalog/mappings/${mappingId}`, data, getAuthHeaders());
    return response.data;
};

export const runCatalogBulkAction = async (data) => {
    const response = await axios.post(`${API_BASE_URL}/catalog/bulk-actions`, data, getAuthHeaders());
    return response.data;
};

export const getCatalogExportReadiness = async (params) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/export-readiness`, {
        ...getAuthHeaders(),
        params: cleanParams(params),
    });
    return response.data;
};

export const downloadCatalogPromAiEnrichment = async (sellerAccountId) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/ai-enrichment/prom-export`, {
        ...getAuthHeaders(),
        params: cleanParams({ seller_account_id: sellerAccountId }),
        responseType: "blob",
    });
    return response;
};

export const getCatalogProductExportReadiness = async (sku, params) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/products/${encodeURIComponent(sku)}/export-readiness`, {
        ...getAuthHeaders(),
        params: cleanParams(params),
    });
    return response.data;
};

export const getCatalogQualityIssues = async (params) => {
    const response = await axios.get(`${API_BASE_URL}/catalog/quality`, {
        ...getAuthHeaders(),
        params: cleanParams(params),
    });
    return response.data;
};
